"use client";
import { motion } from "framer-motion";
import React, { useMemo } from "react";
import { timeState } from "../Timepicker/Timepicker";

const ClockDial = ({
  startTime,
  endTime,
}: {
  startTime: timeState;
  endTime: timeState;
}) => {
  const { hour: startHour, min: startMin } = startTime;
  const { hour: endHour, min: endMin } = endTime;
  const startTimeStr = startHour + ":" + startMin;
  const endTimeStr = endHour + ":" + endMin;

  const timeDifference = calculateTimeDifference(startTimeStr, endTimeStr);
  const timeDifferenceFormatted = timeDifference.formattedOutput;
  const timeDifferenceHrs = timeDifference.hours;
  const timeDifferenceMins = timeDifference.mins;

  const height = 300;
  const width = 300;
  const radius_xl = 100;
  const viewBox = `0 0 ${width} ${height}`;
  const foreGroundCircle = (
    <circle
      cx={width / 2}
      cy={height / 2}
      r={radius_xl}
      fill="white"
      strokeWidth={18}
      stroke="#171717"
    ></circle>
  );
  const progressBg = (
    <path
      d="M150 70
    a80 80 0 1 0 0.1 0z"
      fill="transparent"
      strokeWidth={12}
      stroke="#9747FF"
      opacity={0.2}
    />
  );

  const progressLength = timeDifferenceHrs / 24;
  const progressFilled = (
    <motion.path
      d="M150 70
    a80 80 0 1 0 0.1 0z"
      initial={{ scale: -1 }}
      animate={{ pathLength: progressLength, rotate: ["0deg", "360deg"] }}
      strokeLinecap="round"
      fill="transparent"
      strokeWidth={12}
      stroke="#9747FF"
    />
  );

  const renderHourMarkers = () => {
    const markers: any[] = [];
    const clockRadius = 60;
    for (let i = 1; i <= 12; i++) {
      const angle = (i - 3) * 30;
      const x1 = width / 2 + clockRadius * Math.cos((angle * Math.PI) / 180);
      const y1 = height / 2 + clockRadius * Math.sin((angle * Math.PI) / 180);
      const x2 =
        width / 2 + (clockRadius - 4) * Math.cos((angle * Math.PI) / 180);
      const y2 =
        height / 2 + (clockRadius - 4) * Math.sin((angle * Math.PI) / 180);
      if (i !== 12) {
        markers.push(
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#346FA6"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        );
      }
    }
    return markers;
  };

  const TimeMarkers = useMemo(() => renderHourMarkers(), []);
  const timeTextLength = 100;
  const time = (
    <text
      x={width / 2 - timeTextLength / 2}
      y={height / 2 + 10}
      fontWeight="bold"
      fontSize="1.8rem"
      textAnchor="center"
      textLength={timeTextLength}
    >
      {timeDifferenceFormatted}
    </text>
  );
  return (
    <div>
      <svg
        viewBox={viewBox}
        style={{ backgroundColor: "", height: height, width: width }}
      >
        {foreGroundCircle}
        {progressBg}
        {progressFilled}
        {TimeMarkers}
        {time}
        <image
          x={width / 2 - 10}
          y={194}
          href="https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/Sun.svg"
          height="20"
          width="20"
        />
        <image
          x={width / 2 - 8}
          y={86}
          href="https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/moon.svg"
          height="16"
          width="16"
        />
      </svg>
    </div>
  );
};

export default ClockDial;

export function calculateTimeDifference(startTime, endTime) {
  // Split the time strings into hours and minutes
  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = endTime.split(":").map(Number);

  // Calculate the time in minutes
  const startTimeInMinutes = startHours * 60 + startMinutes;
  const endTimeInMinutes = endHours * 60 + endMinutes;

  // Calculate the time difference in minutes
  const timeDiffInMinutes = endTimeInMinutes - startTimeInMinutes;

  // Convert the time difference to hours and minutes
  let hours = Math.floor(timeDiffInMinutes / 60);
  let minutes = timeDiffInMinutes % 60;

  if (hours < 0) {
    hours = 24 + hours;
  }
  if (minutes < 0) {
    minutes = 60 + minutes;
  }
  // Format the output
  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");

  const formattedOutput = `${formattedHours}h ${formattedMinutes}m`;
  return { formattedOutput: formattedOutput, hours: hours, mins: minutes };
}
